import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useQuery } from 'react-apollo';

import { AlbumWithEventSettings } from '../../../../../../../../../graphql/queries/albums';
import { getCelebratedPersonName } from '../../../../../../AlbumFolder/FreemiumEvents/SpecialtyAlbumGreetingModal/helpers';
import useAlbumSlideshow from '../../../../../AlbumSlideshow/hooks/useAlbumSlideshow';
import IntroSlideMessageConfigurator from './components/IntroSlideMessageConfigurator';
import useOpenAlert from '../../../../../../../../../hooks/useOpenAlert';
import { getErrorString } from '../../../../../../../../../util/normalize-errors';
import EditIcon from '../../../../../../../../../components/icons/EditIcon';
import Button from '../../../../../../../../../components/global/Button';
import { isMobile } from '../../../../../../../../../util/device-detector';
import AutoP from '../../../../../../../../../components/global/AutoP';
import { isIntroLayout } from '../../../../../AlbumSlideshow/AlbumSlideshowDesignerView/helpers';
import { isMemoryBook } from '../../../../../../../../../util/album';

const propTypes = {
  albumId: PropTypes.string.isRequired,
  className: PropTypes.string,
  editable: PropTypes.bool,
  style: PropTypes.object,
};

const IntroSlideLabel = ({
  albumId,
  className,
  editable = false,
  onRemove,
  style = {},
  ...props
}) => {
  const openAlert = useOpenAlert();
  const [saving, setSaving] = useState(false);
  const [messageConfiguratorOpen, setMessageConfiguratorOpen] = useState(false);
  const { data: { album } = {} } = useQuery(AlbumWithEventSettings, {
    variables: {
      id: albumId,
    },
    skip: !albumId,
  });
  const { slideshow, onSlideshowUpdate } = useAlbumSlideshow({ albumId });
  const introLayout =
    slideshow?.intro_layout ?? slideshow?.layouts?.records?.find(isIntroLayout);

  if (!album) {
    return null;
  }

  if (!introLayout && !editable) {
    return null;
  }

  if (!slideshow?.intro_slide_label_enabled && !editable) {
    return null;
  }

  const onIntroSlideMessageUpdate = async ({
    text: newText,
    color: newColor,
    backgroundColor: newBackgroundColor,
  }) => {
    if (saving) {
      return;
    }

    try {
      setSaving(true);
      await onSlideshowUpdate({
        introSlideLabelEnabled: true,
        introSlideText: newText,
        introSlideTextColor: newColor,
        introSlideBackgroundColor: newBackgroundColor,
      });
    } catch (e) {
      openAlert(getErrorString(e));
    } finally {
      setSaving(false);
    }
  };

  const onIntroSlideLabelToggle = async enabled => {
    if (saving) {
      return;
    }

    try {
      setSaving(true);
      await onSlideshowUpdate({
        introSlideLabelEnabled: enabled,
        introSlideText: enabled ? undefined : null,
        introSlideTextColor: enabled ? undefined : null,
        introSlideBackgroundColor: enabled ? undefined : null,
      });
    } catch (e) {
      openAlert(getErrorString(e));
    } finally {
      setSaving(false);
    }
  };

  const text = isMemoryBook(album)
    ? (slideshow?.intro_slide_text ??
      `WELCOME TO\n\n${getCelebratedPersonName(
        album.album_subtype,
        album?.root_album_folder?.event_settings,
        true,
        album?.root_album_folder?.account?.organization_name ??
          album?.root_album_folder?.account?.name,
      )} JOURNEY`)
    : slideshow?.intro_slide_text;

  const disabledBox =
    !slideshow?.intro_slide_label_enabled || (!isMemoryBook(album) && !text);

  const classes = classNames({
    'intro-slide-label': true,
    'semi-transparent': disabledBox,
    [className]: !!className,
    editable,
  });

  const color = slideshow?.intro_slide_text_color ?? '#FFFFFF';
  const backgroundColor = slideshow?.intro_slide_background_color ?? '#1a2e34';

  if (disabledBox && !editable) {
    return null;
  }

  if (disabledBox && editable) {
    return (
      <div
        tabIndex={0}
        role="button"
        className={classes}
        aria-disabled={saving}
        onClick={() => setMessageConfiguratorOpen(true)}
        style={{ ...style, backgroundColor }}
        {...props}
      >
        <p className="text-uppercase text-center mb-0" style={{ color }}>
          <span className="text-sm text-black" style={{ color }}>
            {saving
              ? 'LOADING...'
              : `${isMobile() ? 'TAP' : 'CLICK'} HERE TO NAME YOUR SLIDESHOW`}
          </span>
        </p>
        {messageConfiguratorOpen && (
          <IntroSlideMessageConfigurator
            forceOpen={messageConfiguratorOpen}
            text={text}
            color={color}
            backgroundColor={backgroundColor}
            onClose={() => setMessageConfiguratorOpen(false)}
            onIntroSlideMessageUpdate={onIntroSlideMessageUpdate}
          />
        )}
      </div>
    );
  }

  return (
    <div className={classes} style={{ ...style, backgroundColor }} {...props}>
      {editable && (
        <Button
          className="remove-button"
          disabled={saving}
          style={{ color }}
          onClick={() => onIntroSlideLabelToggle(false)}
        >
          &times;
        </Button>
      )}
      {editable && (
        <Button
          className="edit-button"
          disabled={saving}
          style={{ color }}
          onClick={() => setMessageConfiguratorOpen(true)}
        >
          <EditIcon color={color} size={14} />
        </Button>
      )}
      <div className="intro-slide-text" style={{ color }}>
        <AutoP text={text} />
      </div>
      {messageConfiguratorOpen && (
        <IntroSlideMessageConfigurator
          forceOpen={messageConfiguratorOpen}
          text={text}
          color={color}
          backgroundColor={backgroundColor}
          onClose={() => setMessageConfiguratorOpen(false)}
          onIntroSlideMessageUpdate={onIntroSlideMessageUpdate}
        />
      )}
    </div>
  );
};

IntroSlideLabel.propTypes = propTypes;

export default IntroSlideLabel;
